// Keeps the orbit target (the pan) inside the board plus a margin (see
// panBounds.ts). Runs after OrbitControls.update() every frame and moves the
// target AND the camera by the same eased delta, so zoom and viewing angle are
// untouched. Stands down while a cinematic owns the camera (controls disabled).

import { useFrame } from '@react-three/fiber';
import { type RefObject, useMemo } from 'react';
import { type Extent, type Positioned, panBounds, softPullDelta, tileExtent } from './panBounds.js';

interface ClampControls {
  readonly target: {
    x: number;
    y: number;
    z: number;
    set(x: number, y: number, z: number): unknown;
  };
  enabled: boolean;
}

export function CameraPanClamp({
  tiles,
  seaRings,
  scale,
  controls,
}: {
  readonly tiles: ReadonlyArray<Positioned>;
  /** Hex rings of sea/wall drawn beyond the sim board. */
  readonly seaRings: number;
  /** Large Board scale (1 or 2). */
  readonly scale: number;
  readonly controls: RefObject<ClampControls | null>;
}) {
  const bounds = useMemo<Extent | null>(() => {
    const e = tileExtent(tiles);
    return e ? panBounds(e, seaRings, scale) : null;
  }, [tiles, seaRings, scale]);

  // Priority 0 = same slot as CameraFlight: after OrbitControls.update() (-1).
  useFrame(({ camera }, delta) => {
    const ctl = controls.current;
    if (!bounds || !ctl || !ctl.enabled) return;
    const t = ctl.target;
    const [dx, dz] = softPullDelta(t.x, t.z, bounds, delta);
    if (dx === 0 && dz === 0) return;
    t.set(t.x + dx, t.y, t.z + dz);
    camera.position.x += dx;
    camera.position.z += dz;
  });

  return null;
}
